/**
 * The profile editor's form values, and the mappers between them and the API.
 *
 * Distinct from `CreateSaasProfileSchema` because a form holds what inputs
 * hold: "any" / "latest" sentinels instead of absent fields, the proxy as a
 * toggle plus string sub-fields (`port` included, since it comes from a text
 * input), and empty strings rather than undefined. `settings` and `cookies`
 * use the API schemas unchanged. Nothing here has defaults or transforms, so
 * `ProfileFormInput` and `ProfileFormValues` are the same shape;
 * `profileToFormValues()` supplies the defaults.
 */

import { z } from "zod";
import {
	BROWSER_VERSION_MAX,
	BROWSER_VERSION_MIN,
} from "../constants/index.ts";
import { type Cookie, CookiesSchema } from "./cookies.ts";
import type {
	CreateSaasProfileSchema,
	UpdateSaasProfileSchema,
} from "./index.ts";
import { OsSchema, ProxyTypeSchema } from "./primitives.ts";
import {
	compactProfileSettings,
	type ProfileSettings,
	ProfileSettingsSchema,
	validateSettingsForOs,
} from "./profile-settings.ts";

/** Sentinel for "no preference" in the OS version, device model, and GPU selects. */
export const PROFILE_FORM_ANY = "any";

/** Sentinel for "newest browser major available" in the browser version select. */
export const PROFILE_FORM_LATEST = "latest";

/** Kept for existing imports; the same enum as `ProxyTypeSchema`. */
export const ProfileProxyTypeSchema = ProxyTypeSchema;

const OS_VERSION_PATTERN = /^\d{1,4}(?:\.\d{1,5}){0,3}$/;

const IPV4 = z.ipv4();
const IPV6 = z.ipv6();
const HOSTNAME = z.hostname();

function isProxyHost(value: string): boolean {
	const host = value.replace(/^\[(.*)\]$/, "$1");
	return (
		IPV4.safeParse(host).success ||
		IPV6.safeParse(host).success ||
		HOSTNAME.safeParse(host).success
	);
}

function isAny(value: string): boolean {
	const trimmed = value.trim();
	return trimmed === "" || trimmed === PROFILE_FORM_ANY;
}

export const ProfileFormProxySchema = z.object({
	enabled: z.boolean({ error: "Choose whether to use a proxy" }),
	type: z.enum(ProxyTypeSchema.options, { error: "Choose a proxy protocol" }),
	host: z
		.string({ error: "Proxy host must be text" })
		.trim()
		.max(253, "Proxy host must be 253 characters or less"),
	port: z.string({ error: "Proxy port must be text" }).trim(),
	username: z
		.string({ error: "Proxy username must be text" })
		.max(256, "Proxy username must be 256 characters or less"),
	password: z
		.string({ error: "Proxy password must be text" })
		.max(256, "Proxy password must be 256 characters or less"),
});

export type ProfileFormProxy = z.infer<typeof ProfileFormProxySchema>;

export const ProfileFormSchema = z
	.object({
		name: z
			.string({ error: "Name is required" })
			.trim()
			.min(1, "Name is required")
			.max(100, "Name must be 100 characters or less"),
		notes: z
			.string({ error: "Notes must be text" })
			.max(1000, "Notes must be 1000 characters or less"),
		profileType: z.enum(["persistent", "one-time"], {
			error: "Choose persistent or one-time",
		}),
		os: z.enum(OsSchema.options, { error: "Choose an operating system" }),
		osVersion: z
			.string({ error: "Choose an OS version" })
			.trim()
			.refine(
				(value) => value === PROFILE_FORM_ANY || OS_VERSION_PATTERN.test(value),
				"OS version must be Any or a version like 11 or 14.2",
			),
		browserVersion: z
			.union([z.literal(PROFILE_FORM_LATEST), z.number()], {
				error: "Choose a browser version",
			})
			.superRefine((value, ctx) => {
				if (value === PROFILE_FORM_LATEST) return;
				if (
					!Number.isInteger(value) ||
					value < BROWSER_VERSION_MIN ||
					value > BROWSER_VERSION_MAX
				) {
					ctx.addIssue({
						code: "custom",
						message: `Browser version must be a whole number between ${BROWSER_VERSION_MIN} and ${BROWSER_VERSION_MAX}`,
					});
				}
			}),
		model: z
			.string({ error: "Device model must be text" })
			.trim()
			.max(64, "Device model must be 64 characters or less"),
		gpu: z
			.string({ error: "GPU must be text" })
			.trim()
			.max(128, "GPU must be 128 characters or less"),
		proxy: ProfileFormProxySchema,
		settings: ProfileSettingsSchema,
		cookies: CookiesSchema,
	})
	.superRefine((data, ctx) => {
		const { proxy } = data;
		if (proxy.enabled) {
			if (proxy.host === "") {
				ctx.addIssue({
					code: "custom",
					path: ["proxy", "host"],
					message: "Enter the proxy host",
				});
			} else if (!isProxyHost(proxy.host)) {
				ctx.addIssue({
					code: "custom",
					path: ["proxy", "host"],
					message: "Enter a valid host name or IP address",
				});
			}
			const port = Number(proxy.port);
			if (!/^\d{1,5}$/.test(proxy.port) || port < 1 || port > 65535) {
				ctx.addIssue({
					code: "custom",
					path: ["proxy", "port"],
					message: "Enter a port from 1 to 65535",
				});
			}
			if (proxy.password !== "" && proxy.username === "") {
				ctx.addIssue({
					code: "custom",
					path: ["proxy", "username"],
					message: "Enter the username that goes with this password",
				});
			}
		}
		for (const issue of validateSettingsForOs(data.settings, data.os)) {
			ctx.addIssue({
				code: "custom",
				path: ["settings", ...issue.path],
				message: issue.message,
			});
		}
	})
	.describe("Profile create/edit form");

export type ProfileFormInput = z.input<typeof ProfileFormSchema>;
export type ProfileFormValues = z.output<typeof ProfileFormSchema>;

// ============ Mappers ============

type Os = z.infer<typeof OsSchema>;
type ProxyType = z.infer<typeof ProxyTypeSchema>;

/**
 * Request bodies as the schema's output side: defaulted fields (`profileType`
 * on create, the proxy protocol) are present, which is what the typed client
 * expects and what the mappers below always produce.
 */
export type CreateProfileRequest = z.output<typeof CreateSaasProfileSchema>;
export type UpdateProfileRequest = z.output<typeof UpdateSaasProfileSchema>;

/**
 * Any stored profile the editor can open. Structural, so both the API response
 * type and an app's own profile interface fit.
 */
export interface ProfileFormSource {
	name: string;
	os: string;
	osVersion?: string | null;
	browserVersion: number;
	model?: string | null;
	gpuName?: string | null;
	notes?: string | null;
	profileType?: "persistent" | "one-time";
	proxy?: {
		type: string;
		host: string;
		port: number;
		username?: string;
		password?: string;
	} | null;
	settings?: ProfileSettings | null;
	cookies?: Cookie[] | null;
}

export interface ProfileFormToApiOptions {
	/** Resolves the "latest" sentinel, normally the catalog's newest major. */
	latestBrowserMajor?: number;
}

function isOs(value: string | undefined): value is Os {
	return OsSchema.safeParse(value).success;
}

function isProxyType(value: string): value is ProxyType {
	return ProxyTypeSchema.safeParse(value).success;
}

/** Form values for a stored profile, or the defaults for a new one. */
export function profileToFormValues(
	profile?: ProfileFormSource | null,
	defaults: { os?: Os } = {},
): ProfileFormValues {
	const proxy = profile?.proxy;
	return {
		name: profile?.name ?? "",
		notes: profile?.notes ?? "",
		profileType: profile?.profileType ?? "persistent",
		os: isOs(profile?.os) ? profile.os : (defaults.os ?? "win"),
		osVersion: profile?.osVersion || PROFILE_FORM_ANY,
		browserVersion: profile?.browserVersion ?? PROFILE_FORM_LATEST,
		model: profile?.model || PROFILE_FORM_ANY,
		gpu: profile?.gpuName || PROFILE_FORM_ANY,
		proxy: proxy
			? {
					enabled: true,
					type: isProxyType(proxy.type) ? proxy.type : "http",
					host: proxy.host,
					port: String(proxy.port),
					username: proxy.username ?? "",
					password: proxy.password ?? "",
				}
			: {
					enabled: false,
					type: "http",
					host: "",
					port: "",
					username: "",
					password: "",
				},
		settings: profile?.settings ?? {},
		cookies: profile?.cookies ?? [],
	};
}

/** The concrete major for a form's browser version. Throws on an unresolvable "latest". */
export function resolveFormBrowserVersion(
	value: ProfileFormValues["browserVersion"],
	latestBrowserMajor: number | undefined,
): number {
	if (value !== PROFILE_FORM_LATEST) return value;
	if (latestBrowserMajor === undefined) {
		throw new Error(
			"No browser version is available yet. Choose a specific version.",
		);
	}
	return latestBrowserMajor;
}

function toApiProxy(proxy: ProfileFormProxy) {
	if (!proxy.enabled) return undefined;
	return {
		type: proxy.type,
		host: proxy.host.trim(),
		port: Number(proxy.port),
		username: proxy.username || undefined,
		password: proxy.password || undefined,
	};
}

/** `POST /v1/profiles` body. Empty selections are omitted. */
export function profileFormToApi(
	values: ProfileFormValues,
	options: ProfileFormToApiOptions = {},
): CreateProfileRequest {
	const settings = compactProfileSettings(values.settings);
	return {
		name: values.name.trim(),
		os: values.os,
		osVersion: isAny(values.osVersion) ? undefined : values.osVersion.trim(),
		model:
			values.os === "android" && !isAny(values.model)
				? values.model.trim()
				: undefined,
		gpuName: isAny(values.gpu) ? undefined : values.gpu.trim(),
		browserVersion: resolveFormBrowserVersion(
			values.browserVersion,
			options.latestBrowserMajor,
		),
		notes: values.notes.trim() || undefined,
		profileType: values.profileType,
		proxy: toApiProxy(values.proxy),
		settings: settings ?? undefined,
		cookies: values.cookies.length > 0 ? values.cookies : undefined,
	};
}

/**
 * `PATCH /v1/profiles/:id` body. Every field is sent so a cleared selection
 * really clears (null, or "" for `model`). Pass `includeCookies: false` to
 * leave stored cookies untouched, e.g. when the cookie list was not edited.
 */
export function profileFormToUpdateApi(
	values: ProfileFormValues,
	options: ProfileFormToApiOptions & { includeCookies?: boolean } = {},
): UpdateProfileRequest {
	const settings = compactProfileSettings(values.settings);
	const request: UpdateProfileRequest = {
		name: values.name.trim(),
		os: values.os,
		osVersion: isAny(values.osVersion) ? null : values.osVersion.trim(),
		model:
			values.os === "android" && !isAny(values.model)
				? values.model.trim()
				: "",
		gpuName: isAny(values.gpu) ? null : values.gpu.trim(),
		browserVersion: resolveFormBrowserVersion(
			values.browserVersion,
			options.latestBrowserMajor,
		),
		notes: values.notes.trim(),
		profileType: values.profileType,
		proxy: toApiProxy(values.proxy) ?? null,
		settings,
	};
	if (options.includeCookies !== false) {
		request.cookies = values.cookies.length > 0 ? values.cookies : null;
	}
	return request;
}
